/**
 * @prettier
 */
import * as React from 'react';
import { useState } from 'react';
import { updateOrderTicketRequestedApi } from 'src/api/letseatmanager/order/updateOrderTicketRequestedApi';
import { app2 } from 'src/app2';
import { ContextualMenuItem } from 'src/components/ContextualMenuItem';
import { translate } from 'src/i18n/translate';
import { savePosOrderOffline } from 'src/localStorage/savePosOrderOffline';
import { posReducer } from 'src/reducers/posReducer';
import { useMapOrderItemsToCartItems } from 'src/services/pos/useMapOrderItemsToCartItems';
import { usePrintOrderTicket } from 'src/services/printer/usePrintOrderTicket';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { isDeliveryOrder } from 'src/utils/order/isDeliveryOrder';
import { isEatHereOrder } from 'src/utils/order/isEatHereOrder';
import { findItemsChangedInOrder } from 'src/utils/pos/findItemsChangedInOrder';
import { useHasPrintersToPrint } from 'src/utils/printer/useHasPrintersToPrint';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';
import { useUpdatePosPendingOrder } from 'src/utils/react/useUpdatePosPendingOrder';

export function PrintBillContextualMenuItem({ disabled }: Props): React.ReactElement | null {
    const [loading, setLoading] = useState(false);

    const [printOrderTicket] = usePrintOrderTicket();
    const { updatePosPendingOrder } = useUpdatePosPendingOrder();
    const { hasPrintersToPrint } = useHasPrintersToPrint();
    const mapOrderItemsToCartItems = useMapOrderItemsToCartItems();

    const orderType = useSelector((state) => state.pos.orderType);
    const items = useSelector((state) => state.pos.items);
    const total = useSelector((state) => state.pos.payment?.total);
    const cancelledItems = useSelector((state) => state.pos.cancelledItems);
    const pendingOrder = useSelector((state) => state.pos.pendingOrder);
    const disabledPosPaymentActions = useSelector((state) => state.pos.disabledPosPaymentActions);

    const setPendingOrder = useAction(posReducer.actions.setPendingOrder);
    const setItems = useAction(posReducer.actions.setItems);
    const updateOrder = useAction(app2.actions.updateOrder);

    const itemsToPrint = findItemsChangedInOrder(pendingOrder?.orderItems ?? ([] as any), items);
    const disabledButtons = disabled || (!total && (!itemsToPrint?.length || !cancelledItems.length));
    const disabledPrintTicket = disabledButtons || !hasPrintersToPrint || loading;

    const handlePrintTicket = async () => {
        if (!pendingOrder?.ticketRequestedAt && isEatHereOrder(pendingOrder.orderType)) {
            updateOrderTicketRequestedApi({ orderId: pendingOrder?.orderId });
        }
        await updatePendingOrder();
    };

    const updatePendingOrder = async () => {
        if (loading || disabledPosPaymentActions) return;
        setLoading(true);
        const { response, request } = await updatePosPendingOrder({
            firstName: pendingOrder?.firstName,
            lastName: pendingOrder?.lastName,
            mobileNumber: pendingOrder.customerMobileNumber,
            email: pendingOrder.email,
            customerNote: pendingOrder.customerNote,
        });
        setLoading(false);

        if (!response.ok) {
            if (response.problem === 'NETWORK_ERROR') {
                savePosOrderOffline(request);
                return;
            }
            return alertKnownErrorOrSomethingWentWrong(response);
        }

        setPendingOrder(response.data);
        setItems(mapOrderItemsToCartItems(response.data.orderItems));
        updateOrder({ order: response.data });

        await printOrderTicket(response.data, { forceToPrint: true });
    };

    if (!pendingOrder || isDeliveryOrder(orderType)) return null;

    return <ContextualMenuItem label={translate('Print Bill')} disabled={disabledPrintTicket} onClick={handlePrintTicket} />;
}

type Props = {
    disabled?: boolean;
};
